import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Briefcase, Users, Code } from 'lucide-react';

const useCases = [
  { id: 'personal', icon: User, title: 'Personal', desc: 'Personal productivity, reminders, email management, scheduling.' },
  { id: 'business', icon: Briefcase, title: 'Business', desc: 'Customer support, lead management, invoicing, reporting.' },
  { id: 'agency', icon: Users, title: 'Agency', desc: 'Multi-client bots, team workflows, white-label solutions.' },
  { id: 'developer', icon: Code, title: 'Developer', desc: 'Code review, CI/CD automation, monitoring, testing.' },
];

const ProgressBar = ({ step }) => (
  <div className="font-mono text-xs text-zinc-500 mb-8">
    <div className="flex items-center gap-2 mb-2">
      <span>Step {step}/4</span>
      <span className="text-zinc-700">|</span>
      <span className="text-emerald-400">{['Use Case', 'Workload', 'Messaging', 'Plan'][step - 1]}</span>
    </div>
    <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
      <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${step * 25}%` }} />
    </div>
  </div>
);

export { ProgressBar };

export default function OnboardingStep1() {
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-20">
      <div className="w-full max-w-2xl">
        <ProgressBar step={1} />
        <h1 data-testid="onboarding-step1-title" className="text-2xl md:text-3xl font-bold text-white font-mono mb-2 section-marker">How will you use ClawStinger?</h1>
        <p className="text-zinc-500 text-sm mb-8">This helps us recommend the right plan and configuration.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {useCases.map(uc => {
            const Icon = uc.icon;
            return (
              <button
                key={uc.id}
                data-testid={`onboarding-usecase-${uc.id}`}
                onClick={() => setSelected(uc.id)}
                className={`text-left p-6 border rounded-sm transition-all ${selected === uc.id ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-[#0A0A0A] border-zinc-800 hover:border-zinc-700'}`}
              >
                <Icon size={24} className={`mb-3 ${selected === uc.id ? 'text-emerald-400' : 'text-zinc-500'}`} />
                <h3 className="text-white font-mono font-bold mb-1">{uc.title}</h3>
                <p className="text-zinc-500 text-xs leading-relaxed">{uc.desc}</p>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between">
          <button data-testid="onboarding-skip" onClick={() => navigate('/onboarding/step-2')} className="text-sm font-mono text-zinc-500 hover:text-zinc-300 transition-colors">Skip</button>
          <button data-testid="onboarding-next-1" onClick={() => navigate('/onboarding/step-2')} disabled={!selected} className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-8 py-3 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50">
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
